import React, { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function PhotoGallery({
  images = [],
  children,
  hideGrid = false,
}) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  // swipe X
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // swipe Y
  const [touchStartY, setTouchStartY] = useState(0);
  const [touchCurrentY, setTouchCurrentY] = useState(0);

  // zoom
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // double tap
  const [lastTap, setLastTap] = useState(0);

  // mouse drag
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);

  const openViewer = (i) => {
    setIndex(i);
    setOpen(true);
  };

  const next = () => setIndex((prev) => (prev + 1) % images.length);
  const prev = () =>
    setIndex((prev) => (prev - 1 + images.length) % images.length);

  // ================= TOUCH =================

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      // pinch zoom
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY,
      );

      if (!window._initialDistance) {
        window._initialDistance = distance;
      } else {
        const zoom = distance / window._initialDistance;
        setScale(Math.min(Math.max(zoom, 1), 4));
      }
    } else {
      // swipe
      setTouchEnd(e.touches[0].clientX);
      setTouchCurrentY(e.touches[0].clientY);
    }
  };

  const handleTouchEnd = () => {
    const distanceX = touchStart - touchEnd;

    // left / right
    if (distanceX > 50) next();
    if (distanceX < -50) prev();

    // swipe down to close
    const diffY = touchCurrentY - touchStartY;
    if (diffY > 120 && scale === 1) {
      setOpen(false);
    }

    window._initialDistance = null;
  };

  // ================= DOUBLE TAP =================

  const handleDoubleTap = () => {
    setScale((prev) => (prev === 1 ? 2 : 1));
    setPosition({ x: 0, y: 0 });
  };

  const handleTap = () => {
    const now = Date.now();
    if (now - lastTap < 300) {
      handleDoubleTap();
    }
    setLastTap(now);
  };

  // ================= MOUSE DRAG =================

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setCurrentX(e.clientX);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;

    const distance = startX - currentX;

    if (distance > 50) next();
    if (distance < -50) prev();

    setIsDragging(false);
  };

  // ================= GRID =================

  const renderGrid = () => {
    if (!images.length) return null;

    if (images.length === 1) {
      return (
        <img
          src={images[0]}
          onClick={() => openViewer(0)}
          className="w-full aspect-square object-cover rounded-xl cursor-pointer hover:scale-105 transition"
        />
      );
    }

    if (images.length === 2) {
      return (
        <div className="grid grid-cols-2 gap-2">
          {images.map((img, i) => (
            <img
              key={i}
              src={img}
              onClick={() => openViewer(i)}
              className="w-full aspect-square object-cover rounded-xl cursor-pointer hover:scale-105 transition"
            />
          ))}
        </div>
      );
    }

    if (images.length === 3) {
      return (
        <div className="grid grid-cols-2 gap-2">
          <img
            src={images[0]}
            onClick={() => openViewer(0)}
            className="w-full aspect-square object-cover rounded-xl cursor-pointer hover:scale-105 transition"
          />
          <div className="grid gap-2">
            {images.slice(1).map((img, i) => (
              <img
                key={i}
                src={img}
                onClick={() => openViewer(i + 1)}
                className="w-full aspect-square object-cover rounded-xl cursor-pointer hover:scale-105 transition"
              />
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 gap-2">
        {images.slice(0, 4).map((img, i) => (
          <div key={i} className="relative">
            <img
              src={img}
              onClick={() => openViewer(i)}
              className="w-full aspect-square object-cover rounded-xl cursor-pointer hover:scale-105 transition"
            />
            {i === 3 && images.length > 4 && (
              <div
                onClick={() => openViewer(3)}
                className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-xl font-bold rounded-xl cursor-pointer"
              >
                +{images.length - 4}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ================= UI =================

  return (
    <>
      {/* <div className="mt-6">{renderGrid()}</div> */}
      {hideGrid ? (
        <div onClick={() => openViewer(0)} className="cursor-zoom-in">
          {children}
        </div>
      ) : (
        <div className="mt-6">{renderGrid()}</div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center">
          {/* CLOSE */}
          <button
            onClick={() => setOpen(false)}
            className="absolute top-6 right-6 text-white"
          >
            <X size={28} />
          </button>

          {/* LEFT */}
          <button onClick={prev} className="absolute left-6 text-white">
            <ChevronLeft size={40} />
          </button>

          {/* IMAGE */}
          <AnimatePresence mode="wait">
            <motion.img
              key={index}
              src={images[index]}
              className="max-h-[90%] max-w-[90%] object-contain rounded-lg select-none cursor-grab active:cursor-grabbing"
              style={{
                scale,
                x: position.x,
                y: position.y,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleTap}
              // TOUCH
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              // MOUSE
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
          </AnimatePresence>

          {/* RIGHT */}
          <button onClick={next} className="absolute right-6 text-white">
            <ChevronRight size={40} />
          </button>
        </div>
      )}
    </>
  );
}
